import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { TextMultipleLangItem } from '@adornis/buchungsheld-db/entities/TextMultipleLangItem.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { useGeneratedForm } from '@adornis/generated-form/use-generated-form.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import * as m from '../../../../localization/out/messages.js';
import { subscribeMatchingWeekSettings } from '../../../api/settings.js';
import { MatchingWeekSettings } from '../../../db/settings.js';
import { formatFormError } from '../../components/format-form-error.js';
import { MultilanguageFormattableTextarea } from '../../components/multilanguage-formattable-textarea.js';

export const AdminEditCompanyTerms = stateful(() => {
  const [saving, setSaving] = useState(false, []);
  const [saveSuccess, setSaveSuccess] = useState(false, []);
  const [saveError, setSaveError] = useState<string | null>(null, []);

  const settingsQuery = useQuery(
    () =>
      subscribeMatchingWeekSettings()({
        companyTerms: selectionSet(() => TextMultipleLangItem, 1),
        participationAgreementFileId: 1,
        participationAgreementDate: 1,
      }),
    [],
  );

  const [placeholder] = useState(new MatchingWeekSettings({}));
  const settings = (settingsQuery.value ?? placeholder) as MatchingWeekSettings;
  // re-seeds from the loaded settings; local state so typing doesn't fight the entity binding
  const [agreementDate, setAgreementDate] = useState(settings.participationAgreementDate ?? '', [
    settings.participationAgreementDate,
  ]);

  // Uploads the PDF directly and persists the reference immediately, so the file is safe even
  // if the admin never hits Save. Pass null to remove. Auto-fills the "Stand" date on first upload.
  const handleAgreementFile = async (file: File | null) => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    try {
      if (file) {
        const fileId = await AdornisFile.upload(file);
        settings.participationAgreementFileId = fileId;
        if (!settings.participationAgreementDate) {
          const today = format(new Date(), 'dd.MM.yyyy');
          settings.participationAgreementDate = today;
          setAgreementDate(today);
        }
      } else {
        settings.participationAgreementFileId = null;
      }
      await settings.save();
      setSaveSuccess(true);
    } catch (e: any) {
      console.error(e);
      setSaveError(formatFormError(e, 'company_terms_'));
    } finally {
      setSaving(false);
    }
  };

  const { form, renderField } = useGeneratedForm<MatchingWeekSettings>(
    settings,
    {
      translationPrefix: 'company_terms_',
      validator: null,
    },
    [settings],
  );

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    try {
      const err = await form.validate();
      if (err) {
        setSaveError(formatFormError(err, 'company_terms_'));
        return;
      }
      settings.participationAgreementDate = agreementDate.trim() || null;
      await settings.save();
      setSaveSuccess(true);
    } catch (e: any) {
      console.error(e);
      setSaveError(formatFormError(e, 'company_terms_'));
    } finally {
      setSaving(false);
    }
  };

  return html`
    <div class="p-4 sm:p-8 flex flex-col items-center">
      <div class="max-w-4xl w-full">
        ${
          settingsQuery.loading
            ? html`
                <div class="card bg-base-100 shadow-xl">
                  <div class="card-body items-center justify-center">
                    <span class="loading loading-spinner"></span>
                    <span>${m.admin_legal_loading_settings()}</span>
                  </div>
                </div>
              `
            : ''
        }
        ${
          settingsQuery.error
            ? html`
                <div class="alert alert-error mb-4">
                  <span class="icon-[material-symbols--error]"></span>
                  <span>${settingsQuery.error.message ?? m.admin_legal_load_failed()}</span>
                </div>
              `
            : ''
        }
        ${
          settingsQuery.value
            ? html`
                <div class="card bg-base-100 shadow-xl ${saving ? 'opacity-70 pointer-events-none' : ''}">
                  <div class="card-body">
                    <h2 class="card-title text-2xl mb-4">${m.admin_edit_company_terms_title()}</h2>
                    ${
                      saveSuccess
                        ? html`<div class="alert alert-success">
                            <span class="icon-[material-symbols--check-circle]"></span
                            ><span>${m.admin_legal_save_success()}</span>
                          </div>`
                        : ''
                    }
                    ${
                      saveError
                        ? html`<div class="alert alert-error">
                            <span class="icon-[material-symbols--error]"></span><span>${saveError}</span>
                          </div>`
                        : ''
                    }
                    ${renderField(['companyTerms'], MultilanguageFormattableTextarea)}
                  </div>
                </div>

                <div class="card bg-base-100 shadow-xl mt-4 ${saving ? 'opacity-70 pointer-events-none' : ''}">
                  <div class="card-body">
                    <h2 class="card-title text-2xl mb-1">${m.admin_participation_agreement_title()}</h2>
                    <p class="text-sm text-base-content/70 mb-4">${m.admin_participation_agreement_desc()}</p>

                    ${
                      settings.participationAgreementFileId
                        ? html`
                            <a
                              class="link link-primary text-sm mb-3 inline-flex items-center gap-1 w-fit"
                              href="${AdornisFile.getServeLink(settings.participationAgreementFileId)}"
                              target="_blank"
                              rel="noopener"
                            >
                              <span class="icon-[material-symbols--picture-as-pdf]"></span>
                              ${m.admin_participation_agreement_current()}
                            </a>
                          `
                        : nothing
                    }

                    <div class="flex flex-wrap items-center gap-3">
                      <input
                        type="file"
                        accept="application/pdf"
                        class="file-input file-input-bordered"
                        ?disabled=${saving}
                        @change=${(e: Event) => {
                          const input = e.target as HTMLInputElement;
                          const file = input.files?.[0];
                          if (file) void handleAgreementFile(file);
                          input.value = '';
                        }}
                      />
                      ${saving ? html`<span class="loading loading-spinner loading-sm"></span>` : nothing}
                      ${
                        settings.participationAgreementFileId
                          ? html`<button
                              class="btn btn-ghost btn-sm text-error"
                              ?disabled=${saving}
                              @click=${() => handleAgreementFile(null)}
                            >
                              <span class="icon-[material-symbols--delete]"></span>
                              ${m.admin_participation_agreement_remove()}
                            </button>`
                          : nothing
                      }
                    </div>

                    <label class="form-control w-full max-w-xs mt-4">
                      <div class="label py-1">
                        <span class="label-text">${m.admin_participation_agreement_date_label()}</span>
                      </div>
                      <input
                        type="text"
                        class="input input-bordered"
                        placeholder="TT.MM.JJJJ"
                        .value=${agreementDate}
                        ?disabled=${saving}
                        @input=${(e: Event) => setAgreementDate((e.target as HTMLInputElement).value)}
                      />
                    </label>
                  </div>
                </div>
              `
            : ''
        }
        <div class="flex justify-end gap-4 mt-4">
          <button
            class=${`btn btn-primary ${saving ? 'loading' : ''}`}
            ?disabled=${saving || settingsQuery.loading}
            @click=${handleSave}
          >
            <span class="icon-[material-symbols--save]"></span>
            ${m.save()}
          </button>
        </div>
      </div>
    </div>
  `;
});
